import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { AuthService, RoutingService } from '@spartacus/core';
import { Observable, Subscription } from 'rxjs';
import { filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { Country, Language } from '../../../../core/model';
import { RoutingHelperService } from '../../../../core/routing';
import { BaseSiteService, LanguageService, SiteCountryService } from '../../../../core/site-context';
import { PrincipalConfigurationService, UserFacade } from '../../../../core/user';
import { OrderReturnFacade } from '../../../order-base';

export enum MenuSiteContextMode {
  POP_OVER = 'POP_OVER',
  DROPDOWN = 'DROPDOWN',
}

@Component({
  selector: 'py-menu-site-context',
  templateUrl: './menu-site-context.component.html',
  styleUrls: ['./menu-site-context.component.scss'],
  standalone: false,
})
export class MenuSiteContextComponent implements OnInit, OnDestroy {
  modes: typeof MenuSiteContextMode = MenuSiteContextMode;

  private subscription: Subscription = new Subscription();

  @ViewChild('popOver', { static: false }) popover: NgbPopover;
  @ViewChild('overlayBody', { static: false }) overlayBody: ElementRef<HTMLDivElement>;

  @Input() mode?: MenuSiteContextMode = MenuSiteContextMode.POP_OVER;

  languageSelectorVisible$: Observable<boolean>;
  enableSiteContextSwitch$: Observable<boolean>;
  inEditMode$: Observable<boolean>;
  currentLanguage$: Observable<Language>;
  languages$: Observable<Language[]>;
  activeCountry$: Observable<Country>;
  showDropdown: boolean;

  constructor(
    private languageService: LanguageService,
    private baseSiteService: BaseSiteService,
    private siteCountryService: SiteCountryService,
    private routingService: RoutingService,
    private routingHelperService: RoutingHelperService,
    private authService: AuthService,
    private userService: UserFacade,
    private principalConfigurationService: PrincipalConfigurationService,
    private orderReturnService: OrderReturnFacade,
    private cd: ChangeDetectorRef,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    const activeBaseStore$ = this.baseSiteService.get().pipe(map((baseSite) => baseSite.baseStore));

    this.enableSiteContextSwitch$ = this.principalConfigurationService.isEnabled('enableSiteContextSwitch');
    this.languageSelectorVisible$ = this.routingHelperService.languageSelectorVisible();
    this.activeCountry$ = this.siteCountryService.getActiveCountry();

    this.languages$ = activeBaseStore$.pipe(
      filter((baseStore) => !!baseStore),
      map((baseStore) => baseStore.languages),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.currentLanguage$ = this.languageService.getActive().pipe(
      switchMap((activeLanguageIsocode) => this.languageService.getLanguage(activeLanguageIsocode)),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.initInEditMode();
  }

  selectLanguage(value: string): void {
    this.authService
      .isUserLoggedIn()
      .pipe(take(1))
      .subscribe((isLoggegIn) => {
        if (isLoggegIn) {
          this.userService.changeCustomerSessionLanguage(value);
        } else {
          this.languageService.setActive(value);
          this.reloadActiveCountry();
        }
        this.popover?.close();
      });
  }

  onPopoverShown(): void {
    this.renderer.addClass(this.overlayBody.nativeElement, 'opened');
  }

  onPopoverHidden(): void {
    setTimeout(() => {
      this.renderer.removeClass(this.overlayBody.nativeElement, 'opened');
      this.cd.detectChanges();
    }, 200);
  }

  reloadActiveCountry(): void {
    this.activeCountry$.pipe(take(1)).subscribe((country) => this.siteCountryService.loadCountry(country.isocode));
  }

  languageWithFallback(translation: string, fallback: string): string {
    // "Missing translations" are returned in prod mode as a single non-breaking space
    return translation?.trim() || fallback;
  }

  onOpenChange(open: boolean): void {
    this.showDropdown = open;
  }

  hideDropdown(): void {
    this.showDropdown = false;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private initInEditMode(): void {
    this.inEditMode$ = this.routingService.getParams().pipe(
      map((params) => params['orderCode']),
      filter((orderCode) => !!orderCode),
      switchMap((orderCode) => this.orderReturnService.isOrderReturnDraftActiveForOrder(orderCode))
    );
  }
}
